<template>
  <div v-if="overlay" class="tw-relative" data-test="clamped-text">
    <WdsText
      ref="textRef"
      sm
      secondary
      class="tw-whitespace-pre-wrap"
      :class="{ 'tw-line-clamp-2': !expanded }"
    >
      {{ text }}
    </WdsText>
    <span
      v-if="overflows && !expanded"
      class="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-items-center tw-gap-1 tw-bg-true-gray-50 tw-pl-2"
    >
      <span aria-hidden="true">…</span>
      <WdsButtonText data-test="clamped-text-toggle" @click="expanded = true">
        {{ $t('Show more') }}
      </WdsButtonText>
    </span>
    <WdsButtonText v-if="expanded" data-test="clamped-text-toggle" @click="expanded = false">
      {{ $t('Show less') }}
    </WdsButtonText>
  </div>

  <div v-else class="tw-flex tw-items-end tw-gap-2" data-test="clamped-text">
    <WdsText
      ref="textRef"
      class="tw-min-w-0 tw-flex-1 tw-whitespace-pre-wrap tw-transition-all tw-duration-200"
      :class="{ 'tw-line-clamp-2': !expanded }"
    >
      <span class="tw-font-normal">{{ text }}</span>
    </WdsText>
    <WdsButtonText
      v-if="overflows || expanded"
      class="tw-flex-shrink-0 tw-whitespace-nowrap tw-font-medium"
      data-test="clamped-text-toggle"
      @click="expanded = !expanded"
    >
      {{ expanded ? $t('Show less') : $t('Show more') }}
    </WdsButtonText>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, ref, watch } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { WdsButtonText, WdsText } from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

// The overlay style puts "… Show more" over the end of the clamped text instead of beside it.
const props = defineProps<{ text: string; overlay?: boolean }>()

const textRef = ref<{ $el: HTMLElement } | null>(null)
const expanded = ref(false)
const overflows = ref(false)

function measure() {
  const el = textRef.value?.$el
  if (!el || expanded.value) return

  overflows.value = el.scrollHeight > el.clientHeight + 1
}

onMounted(measure)
useResizeObserver(() => textRef.value?.$el, measure)

watch(
  () => props.text,
  async () => {
    expanded.value = false
    await nextTick()
    measure()
  },
)
</script>
